import type {
  UserWallConfig,
  WallPostRecord,
  WallPostResponseDTO,
  FollowingFeedResult,
  WallNotificationRecord,
  CommentPolicy,
} from '../../../domain/social/wall-contracts.js';
import {
  type LocalWallStore,
  type OutboxItem,
  localWallStore,
} from '../storage/LocalWallStore.js';

export interface WallProfileResponse {
  config: UserWallConfig;
  metrics: {
    postCount: number;
    followersCount: number;
    followingCount: number;
  };
  isRestricted: boolean;
}

export interface WallPostsResponse {
  posts: WallPostResponseDTO[];
  nextCursor?: string;
  isRestricted: boolean;
  fromCache?: boolean;
}

export interface CreatePostPayload {
  text?: string;
  attachments?: any[];
  sourcePost?: WallPostRecord;
  sourceChannelId?: string;
  isQuote?: boolean;
  commentary?: string;
  commentPolicy?: CommentPolicy | 'MUTUALS_ONLY';
}

export interface LikeToggleResponse {
  hasLiked: boolean;
  likeCount: number;
  isQueuedOffline?: boolean;
}

export class WallClient {
  private baseUrl: string;
  private sessionDid: string;
  private localStore: LocalWallStore;

  constructor(
    sessionDid: string = 'did:herx:p_admin',
    baseUrl: string = '/api/v1',
    localStore: LocalWallStore = localWallStore
  ) {
    this.sessionDid = sessionDid;
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.localStore = localStore;
  }

  public setSessionDid(did: string): void {
    this.sessionDid = did;
  }

  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && !navigator.onLine;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    if (this.isOffline()) {
      throw new Error('NETWORK_OFFLINE');
    }

    const headers = new Headers(options.headers || {});
    if (!headers.has('x-caller-did')) {
      headers.set('x-caller-did', this.sessionDid);
    }
    if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        credentials: 'same-origin',
        ...options,
        headers,
        signal: controller.signal,
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error?.message || json.message || `HTTP ${res.status} ${res.statusText}`);
      }

      return json;
    } finally {
      clearTimeout(timeout);
    }
  }

  async getWallProfile(userDid: string): Promise<WallProfileResponse> {
    try {
      const res = await this.request<{ ok: boolean; data: WallProfileResponse }>(`/walls/${encodeURIComponent(userDid)}`);
      await this.localStore.setCachedProfile(userDid, res.data);
      return res.data;
    } catch (err) {
      const cached = await this.localStore.getCachedProfile(userDid);
      if (cached) return cached;
      throw err;
    }
  }

  async getWallPosts(userDid: string, cursor?: string, limit: number = 20): Promise<WallPostsResponse> {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (cursor) params.set('cursor', cursor);

      const res = await this.request<WallPostsResponse>(`/walls/${encodeURIComponent(userDid)}/posts?${params.toString()}`);
      await this.localStore.setCachedPosts(userDid, res.posts);
      return res;
    } catch (err) {
      const cached = await this.localStore.getCachedPosts(userDid);
      if (cached.length > 0) {
        return {
          posts: cached.slice(0, limit),
          isRestricted: false,
          fromCache: true,
        };
      }
      throw err;
    }
  }

  async getFollowingFeed(cursor?: string, limit: number = 20): Promise<FollowingFeedResult> {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (cursor) params.set('cursor', cursor);
      const res = await this.request<{ ok: boolean; data: FollowingFeedResult }>(`/feed/following?${params.toString()}`);
      return res.data;
    } catch (err) {
      const cached = await this.localStore.getCachedPosts();
      return {
        posts: cached.slice(0, limit),
        cursor: undefined,
        isZeroState: cached.length === 0,
        suggestedUsers: [],
      };
    }
  }

  async createPost(userDid: string, payload: CreatePostPayload): Promise<WallPostRecord> {
    try {
      const res = await this.request<{ ok: boolean; post: WallPostRecord }>(`/walls/${encodeURIComponent(userDid)}/posts`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res.post;
    } catch (err) {
      const bundle = await this.localStore.enqueueOutbox<CreatePostPayload>({
        type: 'CREATE_POST',
        targetDid: userDid,
        payload,
      });

      const optimisticPost: WallPostRecord = {
        wallPostId: `outbox_${bundle.id}`,
        authorDid: this.sessionDid,
        authorHandle: this.sessionDid.split(':').pop() || 'self',
        authorDisplayName: 'You (Offline)',
        postType: payload.sourcePost ? (payload.isQuote ? 'QUOTE_RESHARE' : 'RESHARE') : 'ORIGINAL',
        text: payload.text || payload.commentary || '',
        attachments: payload.attachments || [],
        sourcePost: payload.sourcePost,
        originalPostRef: payload.sourcePost
          ? {
              sourceChannelId: payload.sourceChannelId || 'general',
              postId: payload.sourcePost.wallPostId,
              originalAuthorDid: payload.sourcePost.authorDid,
              originalAuthorHandle: payload.sourcePost.authorHandle,
              originalCreatedAt: payload.sourcePost.createdAt,
              sourceVisibility: payload.sourcePost.visibility,
              availability: 'AVAILABLE',
            }
          : undefined,
        commentPolicy: (payload.commentPolicy as CommentPolicy) || 'EVERYONE',
        resharePolicy: 'ALLOWED',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        likeCount: 0,
        replyCount: 0,
        reshareCount: 0,
        status: 'ACTIVE',
        isPinned: false,
        visibility: 'PUBLIC',
      };

      return optimisticPost;
    }
  }

  async toggleLike(postId: string): Promise<LikeToggleResponse> {
    try {
      const res = await this.request<{ ok: boolean; data: LikeToggleResponse }>(`/walls/posts/${encodeURIComponent(postId)}/like`, {
        method: 'POST',
      });
      return res.data;
    } catch (err) {
      await this.localStore.enqueueOutbox({
        type: 'TOGGLE_LIKE',
        targetDid: postId,
        payload: { postId },
      });
      return { hasLiked: true, likeCount: 1, isQueuedOffline: true };
    }
  }

  async followUser(targetDid: string): Promise<{ status: string }> {
    try {
      const res = await this.request<{ ok: boolean; data: { status: string } }>(`/walls/${encodeURIComponent(targetDid)}/follow`, {
        method: 'POST',
        body: JSON.stringify({ followerDid: this.sessionDid }),
      });
      return res.data;
    } catch (err) {
      await this.localStore.enqueueOutbox({
        type: 'FOLLOW',
        targetDid,
        payload: { followerDid: this.sessionDid },
      });
      return { status: 'QUEUED_OFFLINE' };
    }
  }

  async unfollowUser(targetDid: string): Promise<{ status: string }> {
    try {
      const res = await this.request<{ ok: boolean; data: { status: string } }>(`/walls/${encodeURIComponent(targetDid)}/follow`, {
        method: 'DELETE',
        body: JSON.stringify({ followerDid: this.sessionDid }),
      });
      return res.data;
    } catch (err) {
      await this.localStore.enqueueOutbox({
        type: 'UNFOLLOW',
        targetDid,
        payload: { followerDid: this.sessionDid },
      });
      return { status: 'QUEUED_OFFLINE' };
    }
  }

  async getNotifications(userDid?: string): Promise<WallNotificationRecord[]> {
    const path = userDid ? `/walls/${encodeURIComponent(userDid)}/notifications` : '/notifications';
    const res = await this.request<{ ok: boolean; notifications: WallNotificationRecord[] }>(path);
    return res.notifications;
  }

  async getOutboxItems(): Promise<OutboxItem[]> {
    return this.localStore.getOutbox();
  }
}

export const wallClient = new WallClient();
